/**
 * Ending a session.
 *
 * Every route out of a signed-in session goes through here so the session is
 * cleared exactly once, in one place, and the person lands on a page that tells
 * them why they are looking at it.
 *
 *		signOut(SIGN_OUT_REASON.SignedOut)		they chose to leave
 *		signOut(SIGN_OUT_REASON.Idle)			the idle timeout ran out
 *		signOut(SIGN_OUT_REASON.MaxDuration)	the session hit its absolute max length (see config.js's `session` block)
 *		goToSessionExpired()					the auth token is no longer valid (API 401)
 *		goToAccessRemoved()						the account itself can't be used
 *
 * Each of these does a full page load rather than a router push: the app holds
 * the user in module state, so re-bootstrapping is the only way to be sure
 * nothing from the old session is still in memory.
 */

import globalStore from "/globalStore.js";
import { clearActiveMessagesCache } from "/system-messages/system-messages-data.js";
import { clearApprovalsNavCache } from "/module.timesheets/approvals/approvals-store.js";


/**
 * Why a person was returned to the sign-in page. Travels as `?reason=` and is
 * resolved to its notice by /helpers/session-display-values.js.
 */
export const SIGN_OUT_REASON = {
	SignedOut: "SignedOut",
	Idle: "Idle",
	MaxDuration: "MaxDuration",
	SessionExpired: "SessionExpired",
};




//-- Methods

/**
 * Drop the signed-in session - both the in-memory store and localStorage.
 * Called by everything below; exported for the rare caller that needs to clear
 * without navigating.
 */
export function clearSession()
{
	globalStore.currentUser.clearCurrentUser();

	// Per-session caches that outlive the store, so the next person to sign in on this
	// browser can't be shown the previous reader's banners before the first fetch lands.
	clearActiveMessagesCache();
	clearApprovalsNavCache();
}



/**
 * Sign the person out and return them to the sign-in page with a notice.
 *
 * @param {string} reasonCode One of SIGN_OUT_REASON. Defaults to a plain sign-out.
 */
export function signOut(reasonCode = SIGN_OUT_REASON.SignedOut)
{
	clearSession();

	window.location.href = "/login?reason=" + encodeURIComponent(reasonCode);
}



/**
 * The session ran out server-side (the API answered 401 not-authenticated).
 * Straight to the sign-in form with a "your session has ended" notice, so the
 * person can sign in again without an extra click.
 */
export function goToSessionExpired()
{
	signOut(SIGN_OUT_REASON.SessionExpired);
}



/**
 * The account can no longer be used - deactivated, access removed, or some other
 * account-level problem only an administrator can fix. Call this when the API
 * reports the account (rather than the credentials) is the problem.
 */
export function goToAccessRemoved()
{
	clearSession();

	window.location.href = "/access-removed";
}


export default { SIGN_OUT_REASON, clearSession, signOut, goToSessionExpired, goToAccessRemoved };
